import React, { useState, useEffect } from 'react';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Clock, FileCheck, BarChart3, Shield, Briefcase, Brain, ArrowRight, PenTool, Target, Users, Calendar, CheckCheck, Zap, Star, ArrowDownRight, ArrowUpRight, PlayCircle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ComplianceFormData } from '@/types/compliance';
import ComplianceAIForm from '@/components/compliance-ai/ComplianceAIForm';
import ComplianceAIResponse from '@/components/compliance-ai/ComplianceAIResponse';
import { mockComplianceAiResponse } from '@/mocks/complianceAiResponse';
import { API_CONFIG } from '@/constants/env.ts';
import LoadingAnimation from '@/components/ui/loading-animation';
import { motion } from 'framer-motion';
import PageViewTracker from '@/components/analytics/PageViewTracker';

const TeamworkInspiredLanding = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(90);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);
    setTimeRemaining(90);

    try {
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-ai/`;
      console.log('Sending request to compliance AI API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Origin: window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}`
        );
      }

      let responseData = await response.json();
      console.log('API Response:', responseData);

      // Process the response
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = { content: responseData };
        }
      }

      setTimeout(() => {
        setResponse(responseData);
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error instanceof Error) {
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }

      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log('Falling back to mock data due to API error');
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: 'AI-Powered Compliance Assessment',
            summary:
              'Your compliance needs have been analyzed. Here are our recommendations to improve your compliance posture.',
          });
          setIsLoading(false);
        }, 1000);
      } else {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-sans overflow-x-hidden bg-[#FAFAFA]">
      <PageViewTracker pageName="teamwork-inspired-landing" />
      <Helmet>
        <title>Transform Your Compliance Workflow | Verdea Enterprise AI Solutions</title>
        <meta
          name="description"
          content="Take control of your compliance with our AI-powered workflow platform. Reduce costs by 65%, slash error rates by 86%, and regain 240+ hours annually."
        />
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap');
          body {
            font-family: 'Work Sans', sans-serif;
          }
        `}</style>
      </Helmet>

      {/* Custom Header - Teamwork Style */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollPosition > 50 ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'}`}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="text-[#1d1c39] font-bold text-2xl mr-8">
                <span className="text-[#1b6504]">Verdea</span>AI
              </div>
              <nav className="hidden md:flex space-x-1">
                <a href="#features" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">Features</a>
                <a href="#how-it-works" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">How It Works</a>
                <a href="#regulations" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">Regulations</a>
                <a href="#testimonials" className="px-3 py-2 text-[#1d1c39] hover:text-[#1b6504] font-medium">Testimonials</a>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#" className="hidden md:inline-block text-[#1d1c39] font-medium hover:text-[#1b6504]">Login</a>
              <Button 
                className="rounded-full bg-[#1b6504] hover:bg-[#184c06] text-white font-medium px-6 py-2 text-sm"
                onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <div className={`h-${scrollPosition > 50 ? '16' : '24'}`}></div>

      <main className="flex-grow">
        {!isLoading && !response && (
          <>
            {/* Hero Section - Teamwork Style */}
            <section className="relative pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-24 overflow-hidden">
              <div className="absolute inset-0 pointer-events-none z-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.06)_0%,rgba(255,255,255,0)_70%)]"></div>
                <div className="absolute top-0 right-0 h-[800px] w-[800px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(27, 101, 4, 0.4) 0%, rgba(27, 101, 4, 0) 70%)',
                    transform: 'translate(20%, -30%)'
                  }}
                ></div>
                <div className="absolute bottom-0 left-0 h-[600px] w-[600px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(27, 101, 4, 0.3) 0%, rgba(27, 101, 4, 0) 70%)',
                    transform: 'translate(-20%, 30%)'
                  }}
                ></div>
              </div>

              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="flex flex-col-reverse lg:flex-row gap-12 lg:gap-16 items-center">
                  {/* Left side with headline and CTA */}
                  <div className="w-full lg:w-1/2 text-center lg:text-left">
                    <div className="max-w-xl mx-auto lg:mx-0">
                      <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                        <Star className="h-4 w-4 mr-2" /> 
                        Enterprise Compliance Platform
                      </div>
                      <h1 className="text-[2.75rem] md:text-6xl lg:text-7xl font-extrabold mb-6 tracking-tight leading-[1.1] text-[#1d1c39]">
                        <span className="text-[#1b6504]">Compliance</span> that works <span className="inline-block">for you</span>
                      </h1>
                      <p className="text-xl md:text-2xl text-[#1d1c39]/70 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                        Reduce costs by 65%, slash error rates by 86%, and regain 240+ hours annually with our AI-powered compliance workflows.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                        <Button 
                          size="lg" 
                          className="bg-[#1b6504] hover:bg-[#184c06] text-white rounded-full px-8 h-14 text-lg font-medium"
                          onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                          Request a Demo
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="lg"
                          className="rounded-full border-[#1d1c39] text-[#1d1c39] hover:bg-[#1d1c39]/5 px-8 h-14 text-lg font-medium"
                        >
                          View Demo
                          <PlayCircle className="ml-2 h-5 w-5" />
                        </Button>
                      </div>
                      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-lg mx-auto lg:mx-0">
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1b6504]/10 mb-2">
                            <CheckCircle className="h-5 w-5 text-[#1b6504]" />
                          </div>
                          <span className="text-sm text-[#1d1c39]/70 text-center lg:text-left">Free Compliance Analysis</span>
                        </div>
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1b6504]/10 mb-2">
                            <Clock className="h-5 w-5 text-[#1b6504]" />
                          </div>
                          <span className="text-sm text-[#1d1c39]/70 text-center lg:text-left">90% Faster Reviews</span>
                        </div>
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1b6504]/10 mb-2">
                            <FileCheck className="h-5 w-5 text-[#1b6504]" />
                          </div>
                          <span className="text-sm text-[#1d1c39]/70 text-center lg:text-left">Multi-Regulatory Coverage</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side with image */}
                  <div className="w-full lg:w-1/2 relative">
                    <div className="relative rounded-2xl overflow-hidden border border-[#1d1c39]/10 shadow-2xl">
                      <img
                        src="/screens/dashboard_overview.png"
                        alt="Compliance Workflow Dashboard"
                        className="w-full h-auto object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1d1c39]/5 to-transparent"></div>
                      
                      {/* Floating badge - top right */}
                      <div className="absolute top-4 right-4 bg-white rounded-full py-2 px-4 shadow-lg flex items-center">
                        <Shield className="h-5 w-5 text-[#1b6504] mr-2" />
                        <span className="text-sm font-medium">99.8% Compliance Rate</span>
                      </div>
                      
                      {/* Floating badge - bottom left */}
                      <div className="absolute bottom-4 left-4 bg-white rounded-full py-2 px-4 shadow-lg flex items-center">
                        <ArrowUpRight className="h-5 w-5 text-[#1b6504] mr-2" />
                        <span className="text-sm font-medium">65% Cost Reduction</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wave divider */}
              <div className="absolute bottom-0 left-0 right-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" fill="white" className="w-full h-auto">
                  <path d="M0,96L60,90.7C120,85,240,75,360,69.3C480,64,600,64,720,69.3C840,75,960,85,1080,90.7C1200,96,1320,96,1380,96L1440,96L1440,100L1380,100C1320,100,1200,100,1080,100C960,100,840,100,720,100C600,100,480,100,360,100C240,100,120,100,60,100L0,100Z"></path>
                </svg>
              </div>
            </section>

            {/* Logo section */}
            <section className="py-12 bg-white relative">
              <div className="container mx-auto px-4 md:px-8">
                <p className="text-center text-sm uppercase tracking-wider text-[#1d1c39]/50 font-semibold mb-8">Trusted by compliance teams worldwide</p>
                <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
                  <img src="/logos/amazon.png" alt="Amazon" className="h-7 md:h-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src="/logos/circle8.webp" alt="Circle8" className="h-7 md:h-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src="/logos/ei.png" alt="EI" className="h-7 md:h-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src="/logos/ejaso.png" alt="Ejaso" className="h-7 md:h-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src="/logos/greentech.jpg" alt="Greentech" className="h-7 md:h-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                </div>
              </div>
            </section>

            {/* Video Section - Teamwork Style */}
            <section className="py-24 bg-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[#1b6504]/[0.02] z-0"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-3xl mx-auto text-center mb-12">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">
                    See <span className="text-[#1b6504]">VedeaAI</span> in action
                  </h2>
                  <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                    Watch how our platform transforms regulatory compliance from a burden into a competitive advantage.
                  </p>
                </div>

                <div className="relative mx-auto max-w-4xl rounded-2xl overflow-hidden shadow-2xl border border-[#1d1c39]/10">
                  <div className="aspect-w-16 aspect-h-9 bg-[#1d1c39]/5">
                    <img 
                      src="/screens/dashboard_overview.png" 
                      alt="Product demo video" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button className="w-20 h-20 rounded-full bg-white shadow-xl flex items-center justify-center group">
                        <PlayCircle className="h-14 w-14 text-[#1b6504] group-hover:scale-110 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Features Section - Teamwork Style */}
            <section id="features" className="py-24 bg-white">
              <div className="container mx-auto px-4 md:px-8">
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                    <Star className="h-4 w-4 mr-2" /> 
                    POWERFUL FEATURES
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Everything you need in one platform</h2>
                  <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                    Our platform helps organizations maintain compliance across multiple regulatory frameworks and jurisdictions.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[
                    {
                      icon: <Brain className="h-8 w-8 text-[#1b6504]" />,
                      title: "Automated Pre-Approval",
                      description: "Our AI analyzes content in seconds, identifying compliance issues and suggesting fixes before materials go to review."
                    },
                    {
                      icon: <Shield className="h-8 w-8 text-[#1b6504]" />,
                      title: "Risk Detection",
                      description: "Identify potential compliance risks across all channels, from web content to social media and promotional materials."
                    },
                    {
                      icon: <BarChart3 className="h-8 w-8 text-[#1b6504]" />,
                      title: "Streamlined Workflow",
                      description: "Reduce review times by up to 90%, allowing your team to move quickly while maintaining regulatory compliance."
                    },
                    {
                      icon: <Users className="h-8 w-8 text-[#1b6504]" />,
                      title: "Team Collaboration",
                      description: "Enable seamless collaboration across departments with role-based permissions and approval workflows."
                    },
                    {
                      icon: <Calendar className="h-8 w-8 text-[#1b6504]" />,
                      title: "Compliance Calendar",
                      description: "Stay ahead of regulatory deadlines with automated reminders and milestone tracking for all your compliance obligations."
                    },
                    {
                      icon: <CheckCheck className="h-8 w-8 text-[#1b6504]" />,
                      title: "Audit-Ready Reports",
                      description: "Generate comprehensive reports instantly for auditors with complete compliance histories and documentation."
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="bg-white p-8 rounded-2xl border border-[#1d1c39]/10 hover:border-[#1b6504]/30 hover:shadow-xl transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1b6504]/10 mb-6 group-hover:bg-[#1b6504]/20 transition-colors">
                        {feature.icon}
                      </div>
                      <h3 className="text-xl font-bold mb-4 text-[#1d1c39] group-hover:text-[#1b6504] transition-colors">{feature.title}</h3>
                      <p className="text-[#1d1c39]/70">{feature.description}</p>
                      <div className="mt-6 inline-flex items-center font-medium text-[#1b6504] opacity-0 group-hover:opacity-100 transition-opacity">
                        Learn more <ChevronRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* How it works - Teamwork Style */}
            <section id="how-it-works" className="py-24 bg-[#FAFAFA] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.03)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-20">
                  <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                    <Star className="h-4 w-4 mr-2" /> 
                    HOW IT WORKS
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Simplified compliance in three steps</h2>
                  <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                    Our platform transforms how organizations handle regulatory compliance, saving time while reducing risk.
                  </p>
                </div>

                <div className="relative">
                  {/* Connection line */}
                  <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-[#1d1c39]/10"></div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                    {[
                      {
                        number: "01",
                        title: "Upload your materials",
                        description: "Connect your existing documents or upload new materials directly to our secure platform.",
                        icon: <PenTool className="h-8 w-8 text-[#1b6504]" />
                      },
                      {
                        number: "02",
                        title: "AI analysis & suggestions",
                        description: "Our advanced AI reviews your content against relevant regulatory frameworks and identifies potential issues.",
                        icon: <Zap className="h-8 w-8 text-[#1b6504]" />
                      },
                      {
                        number: "03",
                        title: "Implement & track",
                        description: "Apply AI-powered suggestions to ensure compliance and maintain a complete audit trail of all changes.",
                        icon: <Target className="h-8 w-8 text-[#1b6504]" />
                      }
                    ].map((step, index) => (
                      <div key={index} className="relative">
                        <div className="flex flex-col items-center text-center">
                          <div className="w-14 h-14 rounded-full bg-white shadow-lg border border-[#1d1c39]/10 flex items-center justify-center mb-8 relative z-10">
                            {step.icon}
                          </div>
                          <div className="text-7xl font-extrabold text-[#1b6504]/10 absolute top-0 left-1/2 transform -translate-x-1/2 -z-10">
                            {step.number}
                          </div>
                          <h3 className="text-2xl font-bold mb-4 text-[#1d1c39]">{step.title}</h3>
                          <p className="text-[#1d1c39]/70 max-w-xs mx-auto">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-20 text-center">
                  <Button 
                    size="lg" 
                    className="bg-[#1b6504] hover:bg-[#184c06] text-white rounded-full px-8 h-14 text-lg font-medium"
                    onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Started Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </section>

            {/* Regulations Coverage Section */}
            <section id="regulations" className="py-24 bg-white">
              <div className="container mx-auto px-4 md:px-8">
                <div className="max-w-3xl mx-auto">
                  <div className="text-center mb-16">
                    <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                      <Star className="h-4 w-4 mr-2" /> 
                      COMPREHENSIVE COVERAGE
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Multi-regulatory compliance</h2>
                    <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed">
                      Our platform helps organizations maintain compliance across multiple regulatory frameworks and jurisdictions.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { name: "AIFMD", description: "Alternative Investment Fund Managers Directive" },
                      { name: "MiFID II", description: "Markets in Financial Instruments Directive" },
                      { name: "FINRA", description: "Financial Industry Regulatory Authority" },
                      { name: "SEC", description: "Securities and Exchange Commission" },
                      { name: "FCA", description: "Financial Conduct Authority" },
                      { name: "ESMA", description: "European Securities and Markets Authority" },
                      { name: "GDPR", description: "General Data Protection Regulation" },
                      { name: "CCPA/CPRA", description: "California Consumer Privacy Act" }
                    ].map((regulation, index) => (
                      <div
                        key={index}
                        className="bg-[#FAFAFA] p-5 rounded-2xl border border-[#1d1c39]/10 hover:border-[#1b6504]/30 hover:shadow-lg transition-all duration-300 group"
                      >
                        <h3 className="font-bold text-[#1d1c39] group-hover:text-[#1b6504] transition-colors mb-2">{regulation.name}</h3>
                        <p className="text-sm text-[#1d1c39]/70">{regulation.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Metrics section - Teamwork Style */}
            <section className="py-24 bg-[#1d1c39] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(27,101,4,0.15)_0%,rgba(29,28,57,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-3xl mx-auto text-center mb-16">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">The power of AI-driven compliance</h2>
                  <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                    See the real impact our platform has delivered for organizations just like yours.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {[
                    { metric: "65%", description: "Reduced compliance costs", icon: <ArrowDownRight className="h-6 w-6" /> },
                    { metric: "90%", description: "Faster review process", icon: <Clock className="h-6 w-6" /> },
                    { metric: "86%", description: "Lower error rates", icon: <Shield className="h-6 w-6" /> },
                    { metric: "240+", description: "Hours saved annually", icon: <Calendar className="h-6 w-6" /> }
                  ].map((item, index) => (
                    <div 
                      key={index} 
                      className="relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm p-8 group hover:bg-white/15 transition-colors"
                    >
                      <div className="relative z-10">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#1b6504]/20 mb-6 text-[#1b6504]">
                          {item.icon}
                        </div>
                        <div className="text-5xl lg:text-6xl font-extrabold text-white mb-3">{item.metric}</div>
                        <p className="text-lg text-white/80">{item.description}</p>
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#1b6504]/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Testimonial - Teamwork Style */}
            <section id="testimonials" className="py-24 bg-[#FAFAFA] relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.03)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-4xl mx-auto">
                  <div className="text-center mb-16">
                    <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                      <Star className="h-4 w-4 mr-2" /> 
                      CLIENT SUCCESS
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">What our clients say</h2>
                    <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed max-w-2xl mx-auto">
                      Don't just take our word for it. Here's what compliance leaders have to say about our platform.
                    </p>
                  </div>
                  
                  <div className="bg-white p-10 md:p-12 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                    <div className="text-2xl md:text-3xl font-light italic text-[#1d1c39] mb-8 leading-relaxed">
                      "Verdea AI has transformed our compliance operations. What used to take weeks now happens in hours, with fewer errors and at a fraction of the cost. The level of regulatory intelligence built into the platform is remarkable."
                    </div>
                    <div className="flex items-center">
                      <div className="h-16 w-16 rounded-full bg-[#1d1c39]/10 overflow-hidden mr-6 border-2 border-white shadow-md">
                        <img src="/logos/amazon.png" alt="Client" className="h-full w-full object-cover" />
                      </div>
                      <div>
                        <div className="font-bold text-xl text-[#1d1c39]">Maria Rodriguez</div>
                        <div className="text-[#1d1c39]/70">Chief Compliance Officer, Amazon Financial Services</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Form Section - Teamwork Style */}
            <section id="request-demo" className="py-24 bg-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(24,76,6,0.04)_0%,rgba(255,255,255,0)_70%)]"></div>
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-5xl mx-auto">
                  <div className="text-center mb-16">
                    <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-[#1b6504]/10 text-[#1b6504] text-sm font-medium">
                      <Star className="h-4 w-4 mr-2" /> 
                      GET STARTED
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[#1d1c39]">Request your free compliance analysis</h2>
                    <p className="text-lg md:text-xl text-[#1d1c39]/70 leading-relaxed max-w-2xl mx-auto">
                      Tell us about your compliance needs and we'll show you how our AI can transform your workflow.
                    </p>
                  </div>
                  
                  <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                    <ComplianceAIForm 
                      onSubmit={handleFormSubmit} 
                      isLoading={isLoading}
                      error={error}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Final CTA - Teamwork Style */}
            <section className="py-20 bg-[#1b6504] text-white relative overflow-hidden">
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 70%)',
                    transform: 'translate(30%, -50%)'
                  }}
                ></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-10" 
                  style={{
                    background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 70%)',
                    transform: 'translate(-30%, 50%)'
                  }}
                ></div>
              </div>
              
              <div className="container mx-auto px-4 md:px-8 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Transform Your Compliance Workflow Today</h2>
                  <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto leading-relaxed">
                    Get your free compliance analysis today and discover how AI can help your team achieve faster, more accurate regulatory compliance.
                  </p>
                  <Button 
                    size="lg" 
                    className="bg-white text-[#1b6504] hover:bg-white/90 rounded-full px-10 h-14 text-lg font-medium"
                    onClick={() => document.getElementById('request-demo')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}

        {isLoading ? (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                  <LoadingAnimation
                    timeRemaining={timeRemaining}
                    setTimeRemaining={setTimeRemaining}
                    initialTime={90}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : response ? (
          <section className="py-24 bg-[#FAFAFA]">
            <div className="container mx-auto px-4 md:px-8">
              <div className="max-w-5xl mx-auto">
                <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-[#1d1c39]/10">
                  <ComplianceAIResponse
                    response={response}
                    isLoading={isLoading}
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                      setTimeRemaining(90);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </main>

      {/* Teamwork-style Footer */}
      {!isLoading && (
        <footer className="bg-[#1d1c39] text-white pt-16 pb-8">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
              <div>
                <div className="text-2xl font-bold mb-6">
                  <span className="text-[#1b6504]">Verdea</span>AI
                </div>
                <p className="text-white/70 mb-6">
                  AI-powered compliance platform that helps organizations reduce costs, minimize errors, and save time.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="#" className="text-white hover:text-[#1b6504]">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-6">Solutions</h3>
                <ul className="space-y-4">
                  <li><a href="#" className="text-white/70 hover:text-white">AIFMD Compliance</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">CSRD Compliance</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">NIS2 Compliance</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Financial Marketing</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Custom AI Solutions</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-6">Company</h3>
                <ul className="space-y-4">
                  <li><a href="#" className="text-white/70 hover:text-white">About Us</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Careers</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Blog</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Press</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Contact</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-6">Resources</h3>
                <ul className="space-y-4">
                  <li><a href="#" className="text-white/70 hover:text-white">Help Center</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">API Documentation</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Privacy Policy</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Terms of Service</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white">Security</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-white/20 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-white/50 text-sm mb-4 md:mb-0">
                  © 2025 Verdea AI. All rights reserved.
                </p>
                <div className="flex space-x-6">
                  <a href="#" className="text-white/50 hover:text-white text-sm">Privacy Policy</a>
                  <a href="#" className="text-white/50 hover:text-white text-sm">Terms of Service</a>
                  <a href="#" className="text-white/50 hover:text-white text-sm">Cookies</a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default TeamworkInspiredLanding;